/* eslint-disable import/no-unresolved */
/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { Spin, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import DOMPurify from 'dompurify';
import Main from '../../components/layout/Main';
import SEO from '../../components/SEO';
import { getSelectedTranslation, getTranslation } from '../../core/utils/helpers';
import useLanguage from '../../core/language/useLanguage';
import { getSpecialForecastRequest, resetSpecialForecast } from '../../core/horoscope/horoscopeActions';
import useUser from '../../core/user/useUser';
import YellowThemeButton from '../../components/YellowThemeButton';
import history from '../../core/utils/history';
import app from '../../core/constants';
import AiChat from '../../components/AiChat';
import FormattedText from '../../components/AiChat/FormattedText';
import useAi from '../../core/ai/useAi';
import { QUESTION_LIMIT } from '../../core/ai/aiReducer';
import { BlingIcon } from '../../components/icons';
import './specialForecastView.scss';

const SpecialForecastView = () => {
    const { sign, frequency, period, slug } = useParams();
    const { language } = useLanguage();
    const { user } = useUser();
    const dispatch = useDispatch();

    const { specialForecast, isFetchingSpecialForecast, specialForecastError } = useSelector(state => state.horoscope);

    // AI Integration
    const { ai, aiSummaryRequest, aiQuestionRequest, aiClearChat, rateLimitInfo } = useAi();
    const [questionsAsked, setQuestionsAsked] = useState(0);
    const [showAiChat, setShowAiChat] = useState(false);

    // Fetch data on mount and when params change
    useEffect(() => {
        if (sign && frequency && period && slug) {
            dispatch(getSpecialForecastRequest({
                sign,
                frequency,
                period,
                slug,
                userAuthenticated: user?.isLoggedIn,
            }));
        }

        return () => {
            dispatch(resetSpecialForecast());
        };
    }, [sign, frequency, period, slug, user?.isLoggedIn, dispatch]);

    // Scroll to top when params change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    // Clear AI chat when special forecast changes
    useEffect(() => {
        aiClearChat();
        setQuestionsAsked(0);
        setShowAiChat(false);
    }, [sign, frequency, period, slug]);

    // Get translation for current special forecast
    const getForecastTranslation = () => {
        if (!specialForecast?.special_forecast?.translations) return null;
        return getSelectedTranslation(specialForecast.special_forecast.translations, language?.selectedLanguage?.id);
    };

    // Get horoscope display name
    const getHoroscopeName = () => {
        if (!specialForecast?.horoscope) return '';
        return specialForecast.horoscope.name || `${sign} Horoscope`;
    };

    // Get period display string
    const getPeriodDisplay = () => {
        if (!specialForecast?.period) return '';
        return specialForecast.period.display || period;
    };

    // Handle buy button click
    const handleBuyClick = () => {
        if (!user?.isLoggedIn) {
            history.push(`/signup?redirect=/${sign}/special-forecast/${frequency}/${period}/${slug}`);
            return;
        }
        // Redirect to appropriate purchase page based on frequency
        if (frequency === 'yearly') {
            history.push(`/life-events/${sign}`);
        } else {
            history.push(`/horoscope/${frequency}/${sign}`);
        }
    };

    // AI Helper Functions
    const getCurrentForecastText = () => {
        const translation = getForecastTranslation();
        if (!translation) return '';

        let text = `${translation?.name || ''}\n`;
        if (translation?.description) {
            text += `${translation.description}`;
        }
        return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
    };

    const handleGetSummary = () => {
        const content = getCurrentForecastText();
        if (content) {
            aiSummaryRequest({ content });
        }
    };

    const openAiChat = () => {
        if (!ai.get('summary')) {
            handleGetSummary();
        }
        setShowAiChat(true);
    };

    const closeAiChat = () => {
        setShowAiChat(false);
    };

    const handleAskQuestion = (question) => {
        aiQuestionRequest({ question, content: getCurrentForecastText() });
        setQuestionsAsked(prev => prev + 1);
    };

    const handleCopySummary = () => {
        const summary = ai.get('summary');
        if (summary) {
            window.navigator.clipboard.writeText(summary).then(() => {
                message.success('Summary copied to clipboard');
            }).catch(() => {
                message.error('Failed to copy');
            });
        }
    };

    // Question limit applies to everyone
    const questionLimit = QUESTION_LIMIT;

    // Rate limit message
    const getRateLimitMessage = () => {
        if (rateLimitInfo.isDailyLimitReached) {
            return 'Daily limit reached (50 requests/day). Please try again tomorrow.';
        }
        if (rateLimitInfo.isHourlyLimitReached) {
            return 'Hourly limit reached (10 requests/hour). Please try again later.';
        }
        return '';
    };

    // Loading state
    if (isFetchingSpecialForecast) {
        return (
            <Main
                className="special-forecast-page center-align"
                title={<div>Loading...</div>}
                headerTitle={(
                    <div>
                        <img style={{ height: 70, width: 70 }}
                            src="https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg"
                            alt="logo" />
                    </div>
                )}
                menuWidth={350}
                menuPlacement="right"
                showHeader
                displayRightMenu
                showFooter={false}>
                <div className="loaderSpacer">
                    <Spin size="large" />
                </div>
            </Main>
        );
    }

    // Error state
    if (specialForecastError) {
        return (
            <Main
                className="special-forecast-page center-align"
                title={<div>Error</div>}
                headerTitle={(
                    <div>
                        <img style={{ height: 70, width: 70 }}
                            src="https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg"
                            alt="logo" />
                    </div>
                )}
                menuWidth={350}
                menuPlacement="right"
                showHeader
                displayRightMenu
                showFooter={false}>
                <div className="special-forecast-content">
                    <h2>{getTranslation(language, 'error', 'Error')}</h2>
                    <p>{specialForecastError}</p>
                    <YellowThemeButton onClick={() => history.push('/')}>
                        {getTranslation(language, 'go_home', 'Go Home')}
                    </YellowThemeButton>
                </div>
            </Main>
        );
    }

    // No data state
    if (!specialForecast) {
        return null;
    }

    const forecastTranslation = getForecastTranslation();
    const userHasAccess = app.ENABLE_FREE_PREMIUM || specialForecast?.special_forecast?.user_has_access;
    const horoscopeName = getHoroscopeName();
    const periodDisplay = getPeriodDisplay();

    // Get back link based on frequency
    const getBackLink = () => {
        if (frequency === 'yearly') {
            return `/life-events/${sign}`;
        }
        return `/horoscope/${frequency}/${sign}`;
    };

    // SEO description
    const seoDescription = forecastTranslation?.description
        ? `${DOMPurify.sanitize(forecastTranslation.description, { ALLOWED_TAGS: [] }).substring(0, 150)}...`
        : `${forecastTranslation?.name || slug} - ${horoscopeName} Special Forecast from Prakash Astrologer.`;

    // Frequency display
    const frequencyLabel = frequency.charAt(0).toUpperCase() + frequency.slice(1);

    return (
        <>
            <SEO
                title={`${forecastTranslation?.name || slug} - ${horoscopeName} ${frequencyLabel} Special Forecast`}
                description={seoDescription}
                url={`/${sign}/special-forecast/${frequency}/${period}/${slug}`}
                type="article"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'Article',
                    headline: `${forecastTranslation?.name} - ${horoscopeName}`,
                    description: seoDescription,
                    author: {
                        '@type': 'Person',
                        name: 'Prakash Astrologer',
                    },
                    publisher: {
                        '@type': 'Organization',
                        name: 'Prakash Astrologer',
                    },
                }} />
            <Main
                className="special-forecast-page center-align"
                headerTitle={(
                    <div>
                        <img style={{ height: 70, width: 70 }}
                            src="https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg"
                            alt="logo" />
                    </div>
                )}
                menuWidth={350}
                menuPlacement="right"
                showHeader
                displayRightMenu
                showFooter={false}
                backLink={(
                    <div style={{ display: 'flex' }}>
                        <Link to={getBackLink()} className="backlink" style={{ marginBottom: '24px' }} />
                    </div>
                )}>
                <div className="special-forecast-content">
                    <div className="special-forecast-header">
                        <span className="frequency-badge">{frequencyLabel}</span>
                        <h1>{forecastTranslation?.name || slug}</h1>
                        <p className="horoscope-info">{horoscopeName}</p>
                        <p className="period-info">{periodDisplay}</p>
                    </div>

                    <div className="content-wrapper">
                        {/* User has access - show full content */}
                        {userHasAccess && (
                            <>
                                {forecastTranslation?.video_link && (
                                    <div className="video-block">
                                        <iframe
                                            src={`https://www.youtube.com/embed/${forecastTranslation.video_link}`}
                                            title="YouTube video player"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            referrerPolicy="strict-origin-when-cross-origin" />
                                    </div>
                                )}
                                {forecastTranslation?.description && (
                                    // eslint-disable-next-line
                                    <div
                                        className="forecast-description"
                                        // eslint-disable-next-line
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(forecastTranslation.description) }} />
                                )}
                            </>
                        )}

                        {/* User doesn't have access - show locked overlay with preview */}
                        {!userHasAccess && (
                            <div className="locked-content-wrapper">
                                <div className="locked-overlay">
                                    <h3>{getTranslation(language, 'content_locked', 'This Content is Locked')}</h3>
                                    <p>{getTranslation(language, 'subscribe_to_view', 'Subscribe to view the full content')}</p>
                                    <YellowThemeButton onClick={handleBuyClick}>
                                        {user?.isLoggedIn
                                            ? getTranslation(language, 'subscribe_now', 'Subscribe Now')
                                            : getTranslation(language, 'sign_up_to_subscribe', 'Sign Up to Subscribe')}
                                    </YellowThemeButton>
                                </div>
                                <div className="blurred-preview">
                                    {forecastTranslation?.video_link_preview && (
                                        <div className="video-block">
                                            <iframe
                                                src={`https://www.youtube.com/embed/${forecastTranslation.video_link_preview}`}
                                                title="YouTube video player"
                                                frameBorder="0"
                                                referrerPolicy="strict-origin-when-cross-origin" />
                                        </div>
                                    )}
                                    <p className="preview-text">
                                        {forecastTranslation?.description_preview
                                         || `This special forecast covers ${forecastTranslation?.name || slug} for ${horoscopeName}.`}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Main>

            {/* AI Floating Button - only show when user has access */}
            {userHasAccess && (
                <button
                    type="button"
                    className="floating-ai-button"
                    onClick={openAiChat}>
                    <div className="floating-ai-button-text">
                        Summarize <BlingIcon />
                    </div>
                </button>
            )}

            {/* AI Chat Panel */}
            {showAiChat && (
                <div className="floating-ai-chat">
                    <div className="floating-ai-chat-header">
                        <span className="floating-ai-chat-title">AI Summary</span>
                        <button
                            type="button"
                            className="floating-ai-chat-close"
                            onClick={closeAiChat}>
                            &times;
                        </button>
                    </div>

                    {ai.get('isLoadingSummary') && (
                        <div className="ai-loading">
                            <Spin size="small" />
                            <span>AI is thinking...</span>
                        </div>
                    )}

                    {ai.get('error') && !ai.get('isLoadingSummary') && (
                        <div className="ai-error">
                            <p>{ai.get('error')}</p>
                            <button
                                type="button"
                                className="retry-btn"
                                onClick={handleGetSummary}>
                                Retry
                            </button>
                        </div>
                    )}

                    {ai.get('summary') && (
                        <div className="ai-summary-wrapper">
                            <div className="ai-summary">
                                <FormattedText text={ai.get('summary')} />
                            </div>
                            <button
                                type="button"
                                className="copy-summary-btn"
                                onClick={handleCopySummary}
                                title="Copy summary">
                                <CopyOutlined />
                            </button>
                        </div>
                    )}

                    {ai.get('summary') && (
                        <AiChat
                            chatHistory={ai.get('chatHistory')}
                            onSend={handleAskQuestion}
                            isLoading={ai.get('isLoadingAnswer')}
                            questionLimit={questionLimit}
                            questionsAsked={questionsAsked}
                            isRateLimited={rateLimitInfo.isLimitReached}
                            rateLimitMessage={getRateLimitMessage()} />
                    )}
                </div>
            )}
        </>
    );
};

export default SpecialForecastView;
